import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import i18n from '../i18n/i18next';
import { authFetch } from '../lib/authFetch';
import { errorMessage, readJson } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ initialMode }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Mode comes from the route (/signup renders with initialMode="signup"),
  // falling back to ?mode=signup, then to login. Read once as lazy initial
  // state so the user's own toggling is never clobbered by a re-render.
  const [mode, setMode] = useState(
    () => (initialMode === 'signup' || params.get('mode') === 'signup' ? 'signup' : 'login')
  ); // 'login' | 'signup'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [confirmAge, setConfirmAge] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Signup is blocked until the user accepts the terms and confirms their age.
  const consentOk = agreeTerms && confirmAge;
  const submitDisabled = loading || (mode === 'signup' && !consentOk);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setAgreeTerms(false);
    setConfirmAge(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && !consentOk) {
      setError('Please accept the Terms and confirm your age to continue.');
      return;
    }

    setLoading(true);

    const endpoint = mode === 'signup' ? '/auth/register' : '/auth/login';
    const body = mode === 'signup'
      ? {
          name: form.name,
          email: form.email,
          password: form.password,
          terms_accepted: agreeTerms,
          age_affirmed: confirmAge,
        }
      : { email: form.email, password: form.password };

    try {
      const res = await authFetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await readJson(res);

      if (!res.ok) {
        setError(errorMessage(res, data));
        return;
      }

      // Store session token (matching AuthCallback pattern).
      // `secure` only on HTTPS so local http dev still works.
      Cookies.set('session_token', data.session_token, {
        expires: 30,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:',
      });

      // Apply saved language preference if set
      const savedLang = data.user?.language_preference;
      if (savedLang && savedLang !== i18n.language) {
        i18n.changeLanguage(savedLang);
        localStorage.setItem('pangaji_language', savedLang);
      }

      // New users (no projects) go to onboarding; returning users go to dashboard
      try {
        const projRes = await authFetch(`${API}/projects`, { credentials: 'include' });
        const projData = await projRes.json();
        const hasProjects = Array.isArray(projData) && projData.length > 0;
        navigate(hasProjects ? '/dashboard' : '/new-project', { state: { user: data.user }, replace: true });
      } catch {
        navigate('/dashboard', { state: { user: data.user }, replace: true });
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-input {
          width: 100%;
          background: #111;
          border: 1px solid rgba(255,255,255,0.1);
          color: #e5e5e5;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          padding: 12px 14px;
          outline: none;
          transition: border-color 0.18s;
          box-sizing: border-box;
        }
        .login-input:focus { border-color: rgba(232,160,32,0.6); }
        .login-input::placeholder { color: #4a4a4a; }
        .login-btn {
          width: 100%;
          background: #E8A020;
          color: #000;
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          font-size: 0.72rem;
          padding: 14px;
          border: none;
          cursor: pointer;
          transition: background 0.18s, box-shadow 0.18s;
          box-shadow: 0 0 24px rgba(232,160,32,0.3);
        }
        .login-btn:hover:not(:disabled) { background: #f0b030; box-shadow: 0 0 40px rgba(232,160,32,0.55); }
        .login-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        /* The mode switch is a real action, not a footnote. A visitor who
           lands on the login card with no account has to be able to SEE the
           way to sign up — as small grey text it was the step people quit at.
           Square, per the app's no-rounded-corners rule. */
        .login-toggle {
          display: inline-block;
          margin-top: 10px;
          background: none;
          border: 1px solid rgba(232,160,32,0.55);
          color: #E8A020;
          font-family: 'Courier Prime', monospace;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          cursor: pointer;
          text-decoration: none;
          padding: 10px 22px;
          transition: background 0.18s ease, border-color 0.18s ease;
        }
        .login-toggle:hover {
          background: rgba(232,160,32,0.12);
          border-color: #E8A020;
        }
        .login-toggle:focus-visible {
          outline: 2px solid #E8A020;
          outline-offset: 2px;
        }
        .login-back {
          background: none;
          border: none;
          color: #606060;
          font-family: 'Courier Prime', monospace;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          cursor: pointer;
          transition: color 0.18s;
          padding: 0;
        }
        .login-back:hover { color: #E8A020; }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        {/* Subtle radial glow */}
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(232,160,32,0.06) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '400px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <button className="login-back" onClick={() => navigate('/')} style={{ marginBottom: '24px', display: 'inline-block' }}>
              ← Back to Home
            </button>
            <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.4rem', color: '#E8A020', letterSpacing: '0.14em' }}>
              PANGAJI
            </div>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.3em', color: '#606060', marginTop: '6px' }}>
              The Story Engine
            </p>
          </div>

          {/* Card */}
          <div style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.07)', padding: '40px 36px' }}>
            <h1 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '28px', color: '#fff' }}>
              {mode === 'login' ? 'Log In' : 'Create Account'}
            </h1>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {mode === 'signup' && (
                <div>
                  <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080', marginBottom: '6px' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update('name')}
                    required
                    autoComplete="name"
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080', marginBottom: '6px' }}>
                  Email
                </label>
                <input
                  type="email"
                  className="login-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={update('email')}
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080' }}>
                    Password
                  </label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      style={{ background: 'none', border: 'none', fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', color: '#606060', cursor: 'pointer', letterSpacing: '0.08em', padding: 0, transition: 'color 0.18s' }}
                      onMouseOver={e => e.target.style.color = '#E8A020'}
                      onMouseOut={e => e.target.style.color = '#606060'}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  className="login-input"
                  minLength={mode === 'signup' ? 6 : undefined}
                  placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                  value={form.password}
                  onChange={update('password')}
                  required
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
              </div>

              {mode === 'signup' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#a0a0a0', lineHeight: 1.5 }}>
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      required
                      style={{ accentColor: '#E8A020', width: '15px', height: '15px', marginTop: '2px', flexShrink: 0 }}
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#E8A020', textDecoration: 'none' }}>Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#E8A020', textDecoration: 'none' }}>Privacy Policy</a>.
                    </span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#a0a0a0', lineHeight: 1.5 }}>
                    <input
                      type="checkbox"
                      checked={confirmAge}
                      onChange={(e) => setConfirmAge(e.target.checked)}
                      required
                      style={{ accentColor: '#E8A020', width: '15px', height: '15px', marginTop: '2px', flexShrink: 0 }}
                    />
                    <span>I confirm I am 16 or older.</span>
                  </label>
                </div>
              )}

              {error && (
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#f87171', margin: '0', lineHeight: 1.5 }}>
                  {error}
                </p>
              )}

              <button type="submit" className="login-btn" disabled={submitDisabled} style={{ marginTop: '8px' }}>
                {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}
              </button>
            </form>

            {/* Toggle mode */}
            <div style={{ marginTop: '24px', textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: '0.86rem', color: '#9a9a9a' }}>
              {mode === 'login' ? (
                <>New to Pangaji?<br />
                  <button className="login-toggle" onClick={() => switchMode('signup')} data-testid="switch-to-signup">
                    Create a free account
                  </button>
                </>
              ) : (
                <>Already have an account?{' '}
                  <button className="login-toggle" onClick={() => switchMode('login')}>
                    Log in
                  </button>
                </>
              )}
            </div>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
            <a href="/terms" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', color: '#404040', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.1em' }}
              onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#404040'}>
              Terms
            </a>
            <a href="/privacy" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', color: '#404040', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.1em' }}
              onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#404040'}>
              Privacy
            </a>
          </div>
          <p style={{ textAlign: 'center', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', color: '#404040', marginTop: '12px', letterSpacing: '0.05em' }}>
            Made in Nairobi 🇰🇪
          </p>
        </div>
      </div>
    </>
  );
}
